import {
    Card,
    CardContent,
    Typography,
    Box,
    Grid2 as Grid,
    Avatar,
    IconButton
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
    CartesianGrid
} from 'recharts';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';

const formatMonth = (value) => dayjs(value).format('MMM YYYY');

const TooltipContent = ({ active, payload, label }) => {
    if (!active || !payload) return null;

    return (
        <Box
            sx={{
                backgroundColor: '#fff',
                borderRadius: 2,
                p: 1,
                boxShadow: 3
            }}
        >
            <Typography variant="caption" color="text.secondary">
                {formatMonth(label)}
            </Typography>

            {payload.map((item) => (
                <Typography
                    key={item.dataKey}
                    variant="body2"
                    sx={{ color: item.stroke }}
                >
                    {item.name}: {item.value?.toLocaleString()}
                </Typography>
            ))}
        </Box>
    );
};

const Metric = ({ label, value }) => (
    <Box textAlign="center">
        <Typography variant="h3" fontWeight={700} color="primary">
            {value.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
    </Box>
);

const ClinicDetailsCard = ({ clinic }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!clinic) {
        return (
            <Card sx={{ height: '100%', p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {t('management.selectClinic')}
                </Typography>
            </Card>
        );
    }

    return (
        <Card
            sx={{
                height: '100%',
                borderRadius: 0
            }}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                {/* Header */}
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar
                        src={clinic.logo_url}
                        variant="square"
                        sx={{
                            width: 56,
                            height: 56,
                            bgcolor: '#f5f6f8',
                            fontWeight: 700
                        }}
                    >
                        {clinic.name?.[0]}
                    </Avatar>

                    <Box>
                        <Typography variant="h3" fontWeight={700} color="primary">
                            {clinic.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('management.clinicAnalytics')}
                        </Typography>
                    </Box>

                    {/* Spacer */}
                    <Box sx={{ ml: 'auto' }}>
                        <IconButton
                            onClick={() =>
                                navigate('/mgmt/clinic/settings', {
                                    state: { clinic: clinic.name }
                                })
                            }
                        >
                            <SettingsIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Chart */}
                <Box sx={{ flexGrow: 1, mb: 2 }}>
                    <ResponsiveContainer>
                        <LineChart data={clinic.evolution}>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#f0f0f0"
                            />

                            <XAxis
                                dataKey="month"
                                tickFormatter={formatMonth}
                                axisLine={false}
                                tickLine={false}
                                interval="preserveStartEnd"
                                ticks={[
                                    clinic.evolution[0]?.month,
                                    clinic.evolution[clinic.evolution.length - 1]?.month
                                ]}
                                style={{ fill: '#9e9e9e', fontSize: 12 }}
                            />

                            <YAxis yAxisId="patients" hide />
                            <YAxis yAxisId="exercises" hide />

                            <Tooltip content={<TooltipContent />} />
                            <Legend />

                            <Line
                                yAxisId="patients"
                                type="monotone"
                                dataKey="patients"
                                name={t('management.patientsLabel')}
                                stroke="#1976d2"
                                strokeWidth={2.5}
                                dot={false}
                            />

                            <Line
                                yAxisId="exercises"
                                type="monotone"
                                dataKey="exercises"
                                name={t('management.exercises')}
                                stroke="#63a4ff"
                                strokeWidth={2.5}
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>

                {/* Metrics */}
                <Grid container spacing={2}>
                    <Grid size={2}>
                        <Metric label={t('management.doctors')} value={clinic.doctors} />
                    </Grid>
                    <Grid size={2}>
                        <Metric label={t('management.groups')} value={clinic.groups} />
                    </Grid>
                    <Grid size={2}>
                        <Metric
                            label={t('management.activePlans')}
                            value={clinic.activeTrainingPlans}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Metric
                            label={t('management.exercises')}
                            value={clinic.exercisesCompleted}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Metric
                            label={t('management.alerts')}
                            value={clinic.alertsDetected}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Metric
                            label={t('management.questionnaires')}
                            value={clinic.questionnairesAnswered}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default ClinicDetailsCard;
